/**
 * Lazy Analytics Loader
 * Carga scripts tras interacción o idle (mejor FCP/LCP).
 * Incluye: Google Analytics, Metricool, Tally, Meta Pixel.
 */

import { BRAND_META_PIXEL_ID, BRAND_METRICOOL_HASH } from "@/lib/brandConfig";
import { loadMetaPixel } from "@/lib/facebookPixel";

// Env-driven para que un fork de otra marca no herede la propiedad GA4 de ana.
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || "G-QDGH3RF9KY";

let analyticsLoaded = false;

function loadGoogleAnalytics(): void {
  if (document.querySelector('script[src*="googletagmanager.com/gtag"]')) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    functionality_storage: "granted",
    personalization_storage: "denied",
    security_storage: "granted",
    wait_for_update: 2000,
  });

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  script.onload = () => {
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, {
      anonymize_ip: true,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
    });
  };
  document.head.appendChild(script);
}

function loadMetricool(): void {
  if (!BRAND_METRICOOL_HASH) return;
  if (document.querySelector('script[src*="metricool.com"]')) return;

  const script = document.createElement("script");
  script.src = "https://tracker.metricool.com/resources/be.js";
  script.async = true;
  script.onload = () => {
    if (typeof window.beTracker !== "undefined") {
      window.beTracker.t({ hash: BRAND_METRICOOL_HASH });
    }
  };
  document.head.appendChild(script);
}

function loadTally(): void {
  if (document.querySelector('script[src*="tally.so"]')) return;

  const script = document.createElement("script");
  script.src = "https://tally.so/widgets/embed.js";
  script.async = true;
  document.head.appendChild(script);
}

/** Asegura script Tally + re-scan de embeds (p. ej. tras cambiar data-tally-src). */
export function ensureTallyEmbeds(): void {
  loadTally();
  const w = window as unknown as { Tally?: { loadEmbeds: () => void } };
  if (w.Tally?.loadEmbeds) w.Tally.loadEmbeds();
}

/**
 * Meta Pixel (Facebook): carga fbevents.js + init + primer PageView.
 * Lazy: solo se dispara post-interacción para preservar FCP/LCP (los pageviews
 * del SPA en route change los maneja `MetaPixelPageview` en App.tsx).
 */
function loadMetaPixelLazy(): void {
  if (!BRAND_META_PIXEL_ID) return;
  loadMetaPixel(BRAND_META_PIXEL_ID);
}

export function loadAllAnalytics(): void {
  if (analyticsLoaded) return;
  analyticsLoaded = true;

  loadGoogleAnalytics();
  loadMetricool();
  loadTally();
  loadMetaPixelLazy();
}

export function initAnalyticsLoader(): void {
  // Cargar GA de inmediato para que GA4 / Asistente de etiquetas detecten el flujo y envíen page_view
  // sin depender solo del idle de 4s (consent sigue aplicándose antes del config en loadGoogleAnalytics).
  loadGoogleAnalytics();

  const events = ["click", "scroll", "keydown", "touchstart"];

  const loadOnInteraction = () => {
    loadAllAnalytics();
    events.forEach((event) => {
      window.removeEventListener(event, loadOnInteraction, { capture: true });
    });
  };

  events.forEach((event) => {
    window.addEventListener(event, loadOnInteraction, {
      capture: true,
      passive: true,
    });
  });

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(() => loadAllAnalytics(), { timeout: 4000 });
  } else {
    setTimeout(loadAllAnalytics, 4000);
  }
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    beTracker: {
      t: (config: { hash: string }) => void;
    };
    Tally?: { loadEmbeds: () => void };
  }
}
